import { useContext, useState, useEffect } from 'react';
import { ArrowLeft, Save, Search, X, Plus, ChevronDown } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AuthContext from '../../../../../../context/AuthContext';
import { useToast } from '../../../../../../context/ToastContext';
import auditQuestionService from '../../../../../../services/audit_services/auditQuestionService';

export default function AuditQuestionCreate() {
  const { auditPlanId, sectionId } = useParams();
  const navigate = useNavigate();
  const { authTokens } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [auditSection, setAuditSection] = useState(null);
  const [auditPlan, setAuditPlan] = useState(null);
  const [userRoleInPlan, setUserRoleInPlan] = useState(null); // User's role in the audit plan

  // Form data
  const [formData, setFormData] = useState({
    // Basic Information (required for creation)
    question_name: '',
    location: '',
    
    // Optional fields that can be filled later
    requirements: '',
    interview_plan: '',
    question_description: '',
    
    // Related objects
    initial_observations: [{ observation: '' }],
    kpis: [{ kpi_name: '', kpi_value: '', kpi_target: '', kpi_description: '' }]
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Load audit section and plan info from the first API call
    // This will be available when we make the create request
  }, [auditPlanId, sectionId]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Handle dynamic arrays
  const addInitialObservation = () => {
    setFormData(prev => ({
      ...prev,
      initial_observations: [...prev.initial_observations, { observation: '' }]
    }));
  };

  const removeInitialObservation = (index) => {
    setFormData(prev => ({
      ...prev,
      initial_observations: prev.initial_observations.filter((_, i) => i !== index)
    }));
  };

  const updateInitialObservation = (index, value) => {
    setFormData(prev => ({
      ...prev,
      initial_observations: prev.initial_observations.map((obs, i) => 
        i === index ? { observation: value } : obs
      )
    }));
  };

  const addKPI = () => {
    setFormData(prev => ({
      ...prev,
      kpis: [...prev.kpis, { kpi_name: '', kpi_value: '', kpi_target: '', kpi_description: '' }]
    }));
  };

  const removeKPI = (index) => {
    setFormData(prev => ({
      ...prev,
      kpis: prev.kpis.filter((_, i) => i !== index)
    }));
  };

  const updateKPI = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      kpis: prev.kpis.map((kpi, i) => 
        i === index ? { ...kpi, [field]: value } : kpi
      )
    }));
  };

  const validateBasicInfo = () => {
    const newErrors = {};

    if (!formData.question_name.trim()) {
      newErrors.question_name = 'Le nom de la question est requis';
    }

    if (!formData.location.trim()) {
      newErrors.location = 'La localisation est requise';
    }

    if (!formData.question_description.trim()) {
      newErrors.question_description = 'La description de la question est requise';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateBasicInfo()) {
      showError('Veuillez remplir tous les champs requis');
      return;
    }

    setLoading(true);

    try {
      // Format data for API
      const questionData = auditQuestionService.formatQuestionData({
        ...formData,
        // Filter out empty observations and KPIs
        initial_observations: formData.initial_observations.filter(obs => obs.observation.trim()),
        kpis: formData.kpis.filter(kpi => kpi.kpi_name.trim())
      });

      const result = await auditQuestionService.createAuditQuestion(
        authTokens.access, 
        auditPlanId, 
        sectionId, 
        questionData
      );

      if (result.success) {
        showSuccess('Question d\'audit créée avec succès');
        // Navigate back to questions list
        navigate(`/dashboard/audit/audit-plans/${auditPlanId}/sections/${sectionId}/questions`);
      } else {
        showError(result.error || 'Erreur lors de la création de la question d\'audit');
        if (result.errorData) {
          setErrors(result.errorData);
        }
      }
    } catch (error) {
      console.error('Error creating audit question:', error);
      showError('Erreur lors de la création de la question d\'audit');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to={`/dashboard/audit/audit-plans/${auditPlanId}/sections/${sectionId}/questions`}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Créer une question d'audit</h1>
            <p className="text-sm text-gray-600 mt-1">Ajouter une nouvelle question à votre section d'audit</p>
          </div>
        </div>
        <button
          onClick={handleSubmit}
          disabled={loading}
          className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Save className="h-4 w-4 mr-2" />
          {loading ? 'Création en cours...' : 'Créer la question'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Informations de base</h2>
            
            <div className="space-y-4">
              {/* Question Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom de la question <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.question_name}
                  onChange={(e) => handleInputChange('question_name', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors.question_name ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Entrez le nom de la question..."
                />
                {errors.question_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.question_name}</p>
                )}
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Localisation <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors.location ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Entrez la localisation..."
                />
                {errors.location && (
                  <p className="mt-1 text-sm text-red-600">{errors.location}</p>
                )}
              </div>

              {/* Requirements */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Exigences</label>
                <textarea
                  value={formData.requirements}
                  onChange={(e) => handleInputChange('requirements', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Entrez les exigences..."
                />
              </div>

              {/* Interview Plan */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Plan d'entretien</label>
                <textarea
                  value={formData.interview_plan}
                  onChange={(e) => handleInputChange('interview_plan', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Entrez le plan d'entretien..."
                />
              </div>

              {/* Question Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description de la question <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.question_description}
                  onChange={(e) => handleInputChange('question_description', e.target.value)}
                  rows={4}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors.question_description ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Entrez une description détaillée de la question..."
                />
                {errors.question_description && (
                  <p className="mt-1 text-sm text-red-600">{errors.question_description}</p>
                )}
              </div>
            </div>
          </div>

          {/* Initial Observations Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Observations initiales</h2>
              <button
                onClick={addInitialObservation}
                className="inline-flex items-center px-3 py-1 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Plus className="h-4 w-4 mr-1" />
                Ajouter une observation
              </button>
            </div>
            
            <div className="space-y-3">
              {formData.initial_observations.map((observation, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="flex-1">
                    <textarea
                      value={observation.observation}
                      onChange={(e) => updateInitialObservation(index, e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder={`Observation initiale ${index + 1}...`}
                    />
                  </div>
                  {formData.initial_observations.length > 1 && (
                    <button
                      onClick={() => removeInitialObservation(index)}
                      className="p-2 text-red-600 hover:text-red-800"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* KPIs Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">KPIs</h2>
              <button
                onClick={addKPI}
                className="inline-flex items-center px-3 py-1 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Plus className="h-4 w-4 mr-1" />
                Ajouter un KPI
              </button>
            </div>
            
            <div className="space-y-4">
              {formData.kpis.map((kpi, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-gray-700">KPI {index + 1}</h3>
                    {formData.kpis.length > 1 && (
                      <button
                        onClick={() => removeKPI(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Nom du KPI</label>
                      <input
                        type="text"
                        value={kpi.kpi_name}
                        onChange={(e) => updateKPI(index, 'kpi_name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Nom du KPI..."
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Valeur actuelle</label>
                      <input
                        type="text"
                        value={kpi.kpi_value}
                        onChange={(e) => updateKPI(index, 'kpi_value', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Valeur actuelle..."
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Valeur cible</label>
                      <input
                        type="text"
                        value={kpi.kpi_target}
                        onChange={(e) => updateKPI(index, 'kpi_target', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Valeur cible..."
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                      <input
                        type="text"
                        value={kpi.kpi_description}
                        onChange={(e) => updateKPI(index, 'kpi_description', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Description du KPI..."
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Guide de création</h3>
            <div className="space-y-3 text-sm text-gray-600">
              <p>• <strong>Nom de la question</strong>, <strong>Description de la question</strong>, <strong>Localisation</strong>, et <strong>Section d'audit</strong> sont requis pour créer la question.</p>
              <p>• Vous pouvez remplir les autres détails plus tard en modifiant la question.</p>
              <p>• Les observations initiales et les KPIs peuvent être ajoutés maintenant ou plus tard.</p>
              <p>• Les observations et KPIs vides seront ignorés.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}